/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { ResponsiveMode } from '@fluentui/react/lib/ResponsiveMode';
import { ISelectableOption } from '@fluentui/react/lib/SelectableOption';
import { Stack } from '@fluentui/react/lib/Stack';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { needsAdobeSignIn, needsDocusignSignIn } from '../../../apollo/reactiveVariables/EsignProviderVariables';

import { currentProvisioningInfoVar } from '../../../apollo/reactiveVariables/ProvisioningVariables';
import provisioningErrorImg from '../../../assets/error-generic.png';
import provisioningProgressImg from '../../../assets/error-refresh.png';
import invitebird from '../../../assets/need-invite-bird.png';
import { useSetModalDataMutation } from '../../../data/client/mutations/SetModalData.generated';
import { ApprovalRequestType, ModalComponentType, ModalSize } from '../../../data/types.generated';
import { WidgetHost } from '../../../hosts/WidgetHost';
import { EntryPoint, LogCategory, PanelRegion, PanelType, ScenarioName, StepName, SubWorkLoad } from '../../../logger';
import { getThemedIcon, IconName } from '../../../resources/icons/Icons';
import { ApprovalProvisioningInfo, ApprovalProvisioningState, ProvisioningStatusClient } from '../../../service/gen';
import { ApprovalCreator } from '../../../service/gen/esign';
import { newGuid } from '../../../utilities/AppUtils';
import { useCloseKeydown, useNotifySuccessInCreatRequest } from '../../../utilities/CustomHookUtils';
import { isCreateAdobeEnabled, isCreateDocusignEnabled } from '../../../utilities/ECSConfigUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { isTemplateEnabled, stringFormat } from '../../../utilities/Utils';
import { ModalHeader, ScrollBoundary } from '../../common';
import { CreateAdobeApproval } from '../../Esign/CreateAdobeApproval';
import { CreateDocusignApproval } from '../../Esign/CreateDocusignApproval';
import { EsignProviderPageContainer } from '../../Esign/EsignProviderPage/EsignProviderPage.container';
import { ChooseTemplate } from '../ChooseTemplate';
import { CreateApproval } from '../CreateApproval';
import { ErrorPage } from '../ErrorPage';
import { LoadingPage } from '../LoadingPage';
import {
    getEsignProviderDropdownStyles,
    getMobileStackStyles,
    getPivotStyles,
    getStackStyles,
    getStackTokens,
} from './CreateRequest.styles';
import {
    CreateRequestEsignProvider,
    CreateRequestPivotItemKey,
    CreateRequestProps,
    CreateRequestType,
} from './CreateRequest.types';

export const CreateRequest: React.FC<CreateRequestProps> = (props: CreateRequestProps): JSX.Element => {
    const {
        showPivot = true,
        defaultPivotItemKey = CreateRequestPivotItemKey.NewRequest,
        overrideRequestType,
        overrideEsignProvider,
        showNavigationBar,
        onDismiss,
        customFooterComponent,
    } = props;

    const provisioningInfo = useReactiveVar<ApprovalProvisioningInfo>(currentProvisioningInfoVar);

    const host = getAppHost();

    const loc = host.getLocale();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;

    const {
        new_request,
        templates,
        no_chat_message,
        approval_create_loading_text,
        request_type_dropdown_label,
        provider_dropdown_label,
        request_type_basic,
        request_type_esign,
        provider_adobe,
        provider_docusign,
        choose_provider,
        create_task_module_subtitle,
    } = loc.strings;

    const logger = host.getLogger();
    const logCategory = LogCategory.CreateApprovalView;

    const isPersonalApp = (): boolean => onDismiss !== undefined || showNavigationBar;
    const newChat = !isPersonalApp() && host.isNewChat();

    const requestTypeOptions = [
        {
            key: CreateRequestType.Basic,
            text: request_type_basic,
        },
    ];

    if (isCreateAdobeEnabled() || isCreateDocusignEnabled()) {
        requestTypeOptions.push({
            key: CreateRequestType.Esign,
            text: request_type_esign,
        });
    }

    const providerOptions: IDropdownOption[] = [];
    if (isCreateAdobeEnabled()) {
        providerOptions.push({
            key: CreateRequestEsignProvider.AdobeSign,
            text: provider_adobe,
            data: { icon: IconName.adobeCreateIcon },
        });
    }
    if (isCreateDocusignEnabled()) {
        providerOptions.push({
            key: CreateRequestEsignProvider.Docusign,
            text: provider_docusign,
            data: { icon: IconName.docusignCreateIcon },
        });
    }

    // Default to first provider only if there is one and only one provider.
    // If none or all enabled, set to null to display the placeholder correctly.
    const defaultEsignProvider =
        providerOptions.length === 1 ? (providerOptions[0].key as CreateRequestEsignProvider) : null;

    const [loadingPage, setLoadingPage] = React.useState<boolean>(false);
    const [loadingPageMessage, setLoadingPageMessage] = React.useState<string>(approval_create_loading_text);
    const [loadingProvisioningPage, setLoadingProvisioningPage] = React.useState<boolean>(false);
    const [errorPage, setErrorPage] = React.useState<boolean>(false);
    const [errorPageMessage, setErrorPageMessage] = React.useState<string>(null);

    const [requestType, setRequestType] = React.useState(overrideRequestType ?? CreateRequestType.Basic);
    const [esignProvider, setEsignProvider] = React.useState(overrideEsignProvider ?? defaultEsignProvider);
    const [showEsignHeader, setShowEsignHeader] = React.useState<boolean>(true);
    const [showEsignFooter, setShowEsignFooter] = React.useState(true);

    const [title, setTitle] = React.useState<string>(null);
    const [details, setDetails] = React.useState<string>(null);

    const entryPoint = onDismiss || showNavigationBar ? EntryPoint.PersonalApp : EntryPoint.ComposeExtension;
    const [pivotItemKey, setPivotItemKey] = React.useState(defaultPivotItemKey);

    const onRetry = (): void => {
        setErrorPage(false);
        setShowEsignHeader(true);
        setShowEsignFooter(true);
        setModalDataMutation({
            variables: {
                isOpen: true,
                componentType: ModalComponentType.CreateTaskModule,
                componentProps: {
                    entryPoint,
                    defaultPivotItemKey: CreateRequestPivotItemKey.NewRequest,
                },
                size: ModalSize.Medium,
            },
        });
    };

    const onRequestTypeKeyChange = (event: React.FormEvent<HTMLDivElement>, option: IDropdownOption): void =>
        setRequestType(option.key as CreateRequestType);
    const onEsignProviderChange = (event: React.FormEvent<HTMLDivElement>, option: IDropdownOption): void =>
        setEsignProvider(option.key as CreateRequestEsignProvider);

    const modalHeader = <ModalHeader subtitle={create_task_module_subtitle} loc={loc} theme={theme} />;

    React.useEffect((): void => {
        logger.logTraceInfo(
            LogCategory.CreateApprovalView,
            `Loaded create approval inner form. Entry Point: ${entryPoint}`
        );
    }, [logger, entryPoint]);

    useCloseKeydown(entryPoint);

    useNotifySuccessInCreatRequest(entryPoint);

    let shouldCallForProvisioningInfo = false;

    /* The iOS Teams platform has an additional feature that allows users to refresh an app by using the
       'pull down' gesture within the app view. When this action is triggered in the 'create approval' view
       from the personal app the Provisioning info internal state gets deleted.
       In order for the Provisioning info to get fetched again we need to remove the EntryPoint.ComposeExtension
       validation for mobile devices, otherwise the network call will not be executed.
       For Widget we'll always need to get the provisioning info
     */
    if (!getAppHost().isMobile() && !(host instanceof WidgetHost)) {
        if (!newChat && !provisioningInfo && entryPoint === EntryPoint.ComposeExtension) {
            shouldCallForProvisioningInfo = true;
        }
    } else {
        if (!newChat && !provisioningInfo) {
            shouldCallForProvisioningInfo = true;
        }
    }

    React.useEffect((): void => {
        if (requestType !== CreateRequestType.Basic) {
            currentProvisioningInfoVar({ State: ApprovalProvisioningState.Ready });
        } else if (shouldCallForProvisioningInfo) {
            const startTime = performance.now();
            let endTime: number;

            logger.logTraceInfo(logCategory, 'Checking for the provisioning state for the tenant');

            new ProvisioningStatusClient()
                .getProvisioningStatus(newGuid())
                .then((result: ApprovalProvisioningInfo): void => {
                    currentProvisioningInfoVar(result);

                    endTime = performance.now();
                    const elapsedTimeInMilliseconds = endTime - startTime;
                    logger.logTraceInfo(
                        logCategory,
                        `Successfully fetched provisioning info. Provision State: ${result.State}. Time elapsed on provisioning page: ${elapsedTimeInMilliseconds}`
                    );

                    setLoadingProvisioningPage(false);
                    // TODO: temporary nonUItelemetry to debug provisioning status/results later on
                    // remove after stabilization
                    logger.markScenario(ScenarioName.CreateApprovals, 'ProvisioningResult', '', {
                        data: {
                            ProvisioningResult: result.State ?? '',
                            ProvisioningMessage: result.Message ?? '',
                        },
                    });
                })
                .catch((error: any): void => {
                    currentProvisioningInfoVar({ State: ApprovalProvisioningState.Unknown });

                    endTime = performance.now();
                    const elapsedTimeInMilliseconds = endTime - startTime;
                    logger.logTraceWarning(
                        logCategory,
                        `Provisioning status set to Unknown!!!. Time elapsed on provisioning page: ${elapsedTimeInMilliseconds}`
                    );

                    setLoadingProvisioningPage(false);
                    logger.logTraceHttpException('GetProvisioningStatus', error.message, error.status);
                });
            setLoadingProvisioningPage(true);
        }
    }, []);

    const showProvisioningPage =
        provisioningInfo &&
        (provisioningInfo.State === ApprovalProvisioningState.InProgress ||
            provisioningInfo.State === ApprovalProvisioningState.Error);

    const provisioningPage =
        provisioningInfo && provisioningInfo.State === ApprovalProvisioningState.InProgress ? (
            <ErrorPage message={loc.strings.provisioning_in_progress} image={provisioningProgressImg} />
        ) : entryPoint === EntryPoint.ComposeExtension &&
          provisioningInfo &&
          provisioningInfo.State === ApprovalProvisioningState.Error ? (
            <ErrorPage
                message={stringFormat(loc.strings.provisioning_failed, {
                    error: provisioningInfo.Message ?? '',
                })}
                image={provisioningErrorImg}
                onAction={(): void => {
                    currentProvisioningInfoVar({ State: ApprovalProvisioningState.Unknown });
                }}
            />
        ) : null;

    const history = useHistory();
    const [setModalDataMutation] = useSetModalDataMutation();

    const onBack = (): void => {
        logger.logTraceInfo(LogCategory.CreateApprovalView, 'Navigating back to the request page');
        if (entryPoint === EntryPoint.PersonalApp && !getAppHost().isMobile()) {
            setModalDataMutation({
                variables: {
                    isOpen: true,
                    componentType: ModalComponentType.CreateTaskModule,
                    componentProps: {
                        entryPoint,
                        defaultPivotItemKey: CreateRequestPivotItemKey.NewRequest,
                    },
                },
            });
        } else {
            history.goBack();
        }
    };

    const resetProviders = (): void => {
        setRequestType(CreateRequestType.Basic);
        setEsignProvider(defaultEsignProvider);
    };

    const onBackFromProviderPage = overrideRequestType ? onBack : resetProviders;
    const onBackFromCreateApproval = overrideRequestType ? onBack : null;

    const page = loadingPage ? (
        <LoadingPage message={loadingPageMessage} />
    ) : newChat ? (
        <ErrorPage message={no_chat_message} image={invitebird} />
    ) : loadingProvisioningPage ? (
        <LoadingPage />
    ) : showProvisioningPage ? (
        provisioningPage
    ) : errorPage ? (
        <ErrorPage image={provisioningErrorImg} onAction={onRetry} message={errorPageMessage} />
    ) : null;

    const hideMainContent = !!page;

    const pageWithHeader = page ? (
        <div className='topContainer modal-container desktop'>
            {modalHeader}
            <div className='approval-scrollable-content'>{page}</div>
        </div>
    ) : null;

    const onRenderEsignProviderOption = (option: ISelectableOption): JSX.Element => {
        return (
            <>
                {option.data && option.data.icon && (
                    <i className='esign-provider-icon'>{getThemedIcon(option.data.icon)}</i>
                )}
                <span>{option.text}</span>
            </>
        );
    };

    const onRenderEsignProviderOptionTitle = (options: IDropdownOption[]): JSX.Element => {
        const option = options[0];
        return option ? (
            <>
                {option.data && option.data.icon && (
                    <i className='esign-provider-icon'>{getThemedIcon(option.data.icon)}</i>
                )}
                <span>{option.text}</span>
            </>
        ) : null;
    };

    const onEsignIframeDisplay = (): void => {
        setShowEsignHeader(false);
        setShowEsignFooter(false);
        setModalDataMutation({
            variables: {
                isOpen: true,
                componentType: ModalComponentType.CreateTaskModule,
                componentProps: {
                    entryPoint,
                    defaultPivotItemKey: CreateRequestPivotItemKey.NewRequest,
                    customFooterComponent: null,
                    overrideRequestType,
                },
                size: ModalSize.Large,
            },
        });
    };

    const providers = showEsignHeader && requestTypeOptions.length > 1 && !overrideRequestType && (
        <Stack horizontal styles={getStackStyles} tokens={getStackTokens}>
            <Stack.Item>
                <Dropdown
                    className='request-type-dropdown'
                    label={request_type_dropdown_label}
                    responsiveMode={ResponsiveMode.large}
                    selectedKey={requestType}
                    onChange={onRequestTypeKeyChange}
                    options={requestTypeOptions}
                    required
                />
            </Stack.Item>
            <Stack.Item>
                {requestType === CreateRequestType.Esign && (
                    <Dropdown
                        className='esign-provider-dropdown'
                        label={provider_dropdown_label}
                        responsiveMode={ResponsiveMode.large}
                        selectedKey={esignProvider}
                        onChange={onEsignProviderChange}
                        options={providerOptions}
                        onRenderOption={onRenderEsignProviderOption}
                        onRenderTitle={onRenderEsignProviderOptionTitle}
                        placeholder={choose_provider}
                        styles={getEsignProviderDropdownStyles()}
                        required
                    />
                )}
            </Stack.Item>
        </Stack>
    );
    const createForm =
        requestType === CreateRequestType.Basic ? (
            <CreateApproval
                {...props}
                title={title}
                setTitle={setTitle}
                details={details}
                setDetails={setDetails}
                setLoadingPage={setLoadingPage}
                setErrorPage={setErrorPage}
                showErrorPage={errorPage}
                provisioningInfo={provisioningInfo}
                onBack={onBackFromCreateApproval}
            />
        ) : requestType === CreateRequestType.Esign && esignProvider === CreateRequestEsignProvider.AdobeSign ? (
            <CreateAdobeApproval
                {...props}
                title={title}
                setTitle={setTitle}
                details={details}
                setDetails={setDetails}
                setLoadingPage={setLoadingPage}
                setLoadingPageMessage={setLoadingPageMessage}
                setErrorPage={setErrorPage}
                setErrorPageMessage={setErrorPageMessage}
                setLoginPage={needsAdobeSignIn}
                onBack={onBackFromCreateApproval}
                onEsignIframeDisplay={onEsignIframeDisplay}
            />
        ) : requestType === CreateRequestType.Esign && esignProvider === CreateRequestEsignProvider.Docusign ? (
            <CreateDocusignApproval
                {...props}
                title={title}
                setTitle={setTitle}
                details={details}
                setDetails={setDetails}
                setLoadingPage={setLoadingPage}
                setLoadingPageMessage={setLoadingPageMessage}
                setErrorPage={setErrorPage}
                setErrorPageMessage={setErrorPageMessage}
                setLoginPage={needsDocusignSignIn}
                setShowEsignHeader={setShowEsignHeader}
                onBack={onBackFromCreateApproval}
                onEsignIframeDisplay={onEsignIframeDisplay}
            />
        ) : null;

    const handleLinkClick = (item: PivotItem): void => {
        if (CreateRequestPivotItemKey[item.props.itemKey] === CreateRequestPivotItemKey.Template) {
            logger.logUserBiAction(
                SubWorkLoad.AppInteraction,
                ScenarioName.CreateTemplatedApproval,
                StepName.OpenTemplatesPage,
                'click',
                'nav',
                'createApproval',
                'createApprovalPivotItemButton',
                PanelRegion.Modal,
                PanelType.CreateApproval
            );
        } else {
            logger.markScenarioComplete(ScenarioName.CreateTemplatedApproval);
        }

        setPivotItemKey(CreateRequestPivotItemKey[item.props.itemKey]);
    };

    const getTabId = (itemKey: string): string => {
        return `pivot-${itemKey}`;
    };

    const pivot =
        showPivot && showEsignHeader ? (
            <Pivot
                headersOnly
                className='with-modal-side-padding'
                styles={getPivotStyles()}
                onLinkClick={handleLinkClick}
                selectedKey={pivotItemKey}
                getTabId={getTabId}>
                <PivotItem
                    headerText={new_request}
                    ariaLabel={new_request}
                    itemKey={CreateRequestPivotItemKey.NewRequest}
                />
                {
                    // TODO: PivotItem enable flag is not confirmed completely
                    <PivotItem
                        headerText={templates}
                        ariaLabel={templates}
                        itemKey={CreateRequestPivotItemKey.Template}
                    />
                }
            </Pivot>
        ) : null;

    const innerContent =
        pivotItemKey === CreateRequestPivotItemKey.Template ? (
            <Stack>
                <ChooseTemplate entryPoint={entryPoint} />
            </Stack>
        ) : (
            <>
                {providers}
                {createForm}
            </>
        );

    // the modal footer is maintained by createForm (CreateApproval, CreateAdobeApproval, CreateDocusignApproval) itself
    const modalFooter = (!createForm || pivotItemKey === CreateRequestPivotItemKey.Template) && (
        <div className='modal-footer'>
            <Stack horizontal horizontalAlign='start'>
                <div className='button-group'>{customFooterComponent}</div>
            </Stack>
        </div>
    );

    const modalFooterClass =
        pivotItemKey === CreateRequestPivotItemKey.Template && !customFooterComponent
            ? 'with-template-footer'
            : requestType === CreateRequestType.Esign && !showEsignFooter
            ? ''
            : 'with-footer';

    const scrollableClass =
        requestType === CreateRequestType.Esign && !showEsignFooter
            ? 'approval-fill-content'
            : 'approval-scrollable-content';

    const approvalType =
        requestType !== CreateRequestType.Esign
            ? null
            : esignProvider === CreateRequestEsignProvider.AdobeSign
            ? ApprovalRequestType.ESign
            : esignProvider === CreateRequestEsignProvider.Docusign
            ? ApprovalRequestType.Docusign
            : null;

    const content = (
        <EsignProviderPageContainer
            approvalCreator={ApprovalCreator.ChatMessage}
            approvalType={approvalType}
            showBackButton={true}
            showTrialPageButton={true}
            showLearnMoreLink={true}
            onBack={onBackFromProviderPage}>
            {host.isMobile() ? (
                <div hidden={hideMainContent}>{innerContent}</div>
            ) : (
                <div className={`topContainer modal-container desktop ${modalFooterClass}`} hidden={hideMainContent}>
                    {modalHeader}
                    {isTemplateEnabled() && pivot}
                    <div role='tabpanel' aria-labelledby={getTabId(pivotItemKey)} className={scrollableClass}>
                        <ScrollBoundary isTopBoundary />
                        {innerContent}
                        <ScrollBoundary isTopBoundary={false} />
                    </div>
                    {modalFooter}
                </div>
            )}
        </EsignProviderPageContainer>
    );

    logger.useCreateScenarioEffect(
        ScenarioName.CreateApprovals,
        getAppHost().getPersonalAppEntryPoint(),
        entryPoint,
        PanelRegion.Modal,
        PanelType.CreateApproval
    );

    return host.isMobile() ? (
        <Stack styles={getMobileStackStyles}>
            {page}
            {content}
        </Stack>
    ) : (
        <>
            {pageWithHeader}
            {(!hideMainContent || requestType === CreateRequestType.Esign) && content}
        </>
    );
};
